import React, { useState, useRef } from 'react';
import { Edit, Trash2, Phone, Calendar, FileText, Image, Star, X, Plus, UserPlus, ArrowRight, StickyNote, FolderOpen, Clock, CheckSquare, Square, Mail, PhoneCall, Contact, Upload, Download, AlertCircle, CheckCircle, Send, Briefcase } from 'lucide-react';

import axios, { API } from './apiClient';

// Auto-attach Bearer token for cross-origin auth
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ExpandableEntityCard = ({ entity, type, color, onEdit, onDelete, onConvert, convertLabel, onSave, showRating, templates }) => {
  const [showJobs, setShowJobs] = useState(false);
  const [showCallbacks, setShowCallbacks] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [showAddCallback, setShowAddCallback] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobForm, setJobForm] = useState({ job_name: '', description: '', status: 'pending', start_date: '', end_date: '', timeline: '', contact_person: '', blueprints: '', drawings: '' });
  const [callbackForm, setCallbackForm] = useState({ callback_date: '', callback_time: '', contact_person: '', phone: '', email: '', notes: '', status: 'scheduled' });
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', job_title: '', email: '', phone: '', notes: '', is_primary: false });
  const [jobNote, setJobNote] = useState('');
  const [generalNote, setGeneralNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Info section toggles
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showBusinessInfo, setShowBusinessInfo] = useState(false);
  const [showSalesInfo, setShowSalesInfo] = useState(false);
  const [showOtherInfo, setShowOtherInfo] = useState(false);
  
  // File upload and timeline states
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileCategory, setFileCategory] = useState('document');
  const [timelineForm, setTimelineForm] = useState({ update_type: 'progress', description: '' });
  const [showTimelineForm, setShowTimelineForm] = useState(false);
  const fileInputRef = useRef(null);

  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', btn: 'bg-purple-600 hover:bg-purple-700' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', btn: 'bg-orange-600 hover:bg-orange-700' }
  };
  const cc = colorClasses[color];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // JOB FUNCTIONS
  const addJob = async () => {
    if (!jobForm.job_name.trim()) return;
    setIsSubmitting(true);
    try {
      const blueprints = jobForm.blueprints.split(',').map(d => d.trim()).filter(d => d);
      const drawings = jobForm.drawings.split(',').map(d => d.trim()).filter(d => d);
      await axios.post(`${API}/admin/${type}s/${entity.id}/jobs`, { ...jobForm, blueprints, drawings }, {});
      setJobForm({ job_name: '', description: '', status: 'pending', start_date: '', end_date: '', timeline: '', contact_person: '', blueprints: '', drawings: '' });
      setShowAddJob(false);
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await axios.delete(`${API}/admin/${type}s/${entity.id}/jobs/${jobId}`, {});
      setSelectedJob(null);
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  const addJobNote = async (jobId) => {
    if (!jobNote.trim()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/${type}s/${entity.id}/jobs/${jobId}/notes`, { note: jobNote }, {});
      setJobNote('');
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  // FILE UPLOAD FUNCTION
  const uploadJobFile = async (jobId, file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_category', fileCategory);
      await axios.post(`${API}/admin/${type}s/${entity.id}/jobs/${jobId}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      onSave();
    } catch (error) { 
      if (process.env.NODE_ENV === 'development') console.error('Error uploading file:', error); 
      alert('Failed to upload file. Please try again.');
    }
    finally { 
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteJobFile = async (jobId, fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API}/admin/${type}s/${entity.id}/jobs/${jobId}/files/${fileId}`, {});
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  // TIMELINE FUNCTIONS
  const addTimelineUpdate = async (jobId) => {
    if (!timelineForm.description.trim()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/${type}s/${entity.id}/jobs/${jobId}/timeline`, timelineForm, {});
      setTimelineForm({ update_type: 'progress', description: '' });
      setShowTimelineForm(false);
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  const deleteTimelineUpdate = async (jobId, updateId) => {
    if (!window.confirm('Delete this timeline update?')) return;
    try {
      await axios.delete(`${API}/admin/${type}s/${entity.id}/jobs/${jobId}/timeline/${updateId}`, {});
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  // CALLBACK FUNCTIONS
  const addCallback = async () => {
    if (!callbackForm.callback_date) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/${type}s/${entity.id}/callbacks`, callbackForm, {});
      setCallbackForm({ callback_date: '', callback_time: '', contact_person: '', phone: '', email: '', notes: '', status: 'scheduled' });
      setShowAddCallback(false);
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  const deleteCallback = async (callbackId) => {
    if (!window.confirm('Delete this callback?')) return;
    try {
      await axios.delete(`${API}/admin/${type}s/${entity.id}/callbacks/${callbackId}`, {});
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  // CONTACT FUNCTIONS
  const addContact = async () => {
    if (!contactForm.first_name.trim() || !contactForm.last_name.trim()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/${type}s/${entity.id}/contacts`, contactForm, {});
      setContactForm({ first_name: '', last_name: '', job_title: '', email: '', phone: '', notes: '', is_primary: false });
      setShowAddContact(false);
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/${type}s/${entity.id}/contacts/${contactId}`, {});
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  // GENERAL NOTE FUNCTIONS
  const addGeneralNote = async () => {
    if (!generalNote.trim()) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/${type}s/${entity.id}/notes`, { text: generalNote }, {});
      setGeneralNote('');
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
    finally { setIsSubmitting(false); }
  };

  const deleteGeneralNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/admin/${type}s/${entity.id}/notes/${noteId}`, {});
      onSave();
    } catch (error) { if (process.env.NODE_ENV === 'development') console.error('Error:', error); }
  };

  const hasExpandedSection = showJobs || showCallbacks || showTemplates || showContacts || showContactInfo || showBusinessInfo || showSalesInfo || showOtherInfo;

  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${hasExpandedSection ? cc.border : ''}`}>
      {/* Header Row */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <span className={`font-bold ${cc.text} text-lg`}>{entity.id}</span>
            <span className="font-semibold text-lg">{entity.company_name}</span>
            {entity.corporation && <span className="text-sm text-slate-500">({entity.corporation})</span>}
            {showRating && entity.rating && <span className="flex items-center text-xs bg-yellow-50 px-2 py-0.5 rounded"><Star size={12} className="text-yellow-500 mr-0.5" />{entity.rating}/10</span>}
          </div>
          <div className="flex items-center space-x-3">
            {onConvert && <button onClick={onConvert} className="text-purple-500 hover:text-purple-700 text-sm font-medium flex items-center"><ArrowRight size={16} className="mr-1" />{convertLabel}</button>}
            <button onClick={onEdit} className="text-orange-500 hover:text-orange-700"><Edit size={18} /></button>
            <button onClick={onDelete} className="text-red-500 hover:text-red-700"><Trash2 size={18} /></button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            <span>{entity.first_name} {entity.last_name}</span>
            <span className="mx-2">•</span><span>{entity.email}</span>
            <span className="mx-2">•</span><span>{entity.phone}</span>
            <span className="mx-2">•</span><span className="text-slate-400">Entered: {formatDate(entity.created_at)}</span>
          </div>
          <div className="flex items-center space-x-2 text-xs">
            <span className={`px-2 py-1 rounded ${entity.jobs?.length > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{entity.jobs?.length || 0} Jobs</span>
            <span className={`px-2 py-1 rounded ${entity.callbacks?.length > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-500'}`}>{entity.callbacks?.length || 0} Callbacks</span>
            <span className={`px-2 py-1 rounded ${entity.contacts?.length > 0 ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-500'}`}>{entity.contacts?.length || 0} Contacts</span>
          </div>
        </div>

        {/* CHECKBOXES FOR ALL SECTIONS */}
        <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t">
          {/* Info Sections */}
          <button onClick={() => setShowContactInfo(!showContactInfo)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showContactInfo ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showContactInfo ? cc.text + ' font-medium' : 'text-slate-600'}>Contact Info</span>
          </button>
          <button onClick={() => setShowBusinessInfo(!showBusinessInfo)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showBusinessInfo ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showBusinessInfo ? cc.text + ' font-medium' : 'text-slate-600'}>Business Info</span>
          </button>
          <button onClick={() => setShowSalesInfo(!showSalesInfo)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showSalesInfo ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showSalesInfo ? cc.text + ' font-medium' : 'text-slate-600'}>Sales Info</span>
          </button>
          <button onClick={() => setShowOtherInfo(!showOtherInfo)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showOtherInfo ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showOtherInfo ? cc.text + ' font-medium' : 'text-slate-600'}>Other Info</span>
          </button>
          <span className="text-slate-300">|</span>
          {/* Embedded Data Sections */}
          <button onClick={() => setShowJobs(!showJobs)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showJobs ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showJobs ? cc.text + ' font-medium' : 'text-slate-600'}>Jobs</span>
          </button>
          <button onClick={() => setShowCallbacks(!showCallbacks)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showCallbacks ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showCallbacks ? cc.text + ' font-medium' : 'text-slate-600'}>Callbacks</span>
          </button>
          <button onClick={() => setShowTemplates(!showTemplates)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showTemplates ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showTemplates ? cc.text + ' font-medium' : 'text-slate-600'}>Templates</span>
          </button>
          <button onClick={() => setShowContacts(!showContacts)} className="flex items-center space-x-2 text-sm hover:text-slate-800">
            {showContacts ? <CheckSquare size={18} className={cc.text} /> : <Square size={18} className="text-slate-400" />}
            <span className={showContacts ? cc.text + ' font-medium' : 'text-slate-600'}>Contacts</span>
          </button>
        </div>
      </div>

      {/* CONTACT INFO SECTION */}
      {showContactInfo && (
        <div className={`border-t ${cc.border} p-4 ${cc.bg}`}>
          <h4 className="font-semibold mb-3 flex items-center"><Phone size={16} className="mr-2" />Contact Information</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{entity.phone || 'N/A'}</span></div>
            <div><span className="text-slate-500">Secondary Phone:</span> <span className="font-medium">{entity.secondary_phone || 'N/A'}</span></div>
            <div><span className="text-slate-500">Email:</span> <span className="font-medium">{entity.email || 'N/A'}</span></div>
            <div><span className="text-slate-500">Secondary Email:</span> <span className="font-medium">{entity.secondary_email || 'N/A'}</span></div>
            <div><span className="text-slate-500">Fax:</span> <span className="font-medium">{entity.fax || 'N/A'}</span></div>
            <div><span className="text-slate-500">Website:</span> <span className="font-medium">{entity.website || 'N/A'}</span></div>
            <div className="col-span-2"><span className="text-slate-500">Address:</span> <span className="font-medium">{entity.address ? `${entity.address}, ${entity.city || ''} ${entity.state || ''} ${entity.zip_code || ''}` : 'N/A'}</span></div>
          </div>
        </div>
      )}

      {/* BUSINESS INFO SECTION */}
      {showBusinessInfo && (
        <div className={`border-t ${cc.border} p-4 ${cc.bg}`}>
          <h4 className="font-semibold mb-3 flex items-center"><Briefcase size={16} className="mr-2" />Business Information</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Budget:</span> <span className="font-medium">{entity.budget || 'N/A'}</span></div>
            <div><span className="text-slate-500">Project Type:</span> <span className="font-medium">{entity.project_type || 'N/A'}</span></div>
            <div><span className="text-slate-500">Square Footage:</span> <span className="font-medium">{entity.square_footage || 'N/A'}</span></div>
            <div><span className="text-slate-500">Stories:</span> <span className="font-medium">{entity.number_of_stories || 'N/A'}</span></div>
            <div><span className="text-slate-500">Timeline:</span> <span className="font-medium">{entity.timeline_urgency || 'N/A'}</span></div>
            <div><span className="text-slate-500">Grade:</span> <span className="font-medium">{entity.grade ? `${entity.grade}/10` : 'N/A'}</span></div>
          </div>
        </div>
      )}

      {/* SALES INFO SECTION */}
      {showSalesInfo && (
        <div className={`border-t ${cc.border} p-4 ${cc.bg}`}>
          <h4 className="font-semibold mb-3 flex items-center"><Star size={16} className="mr-2" />Sales Information</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Source:</span> <span className="font-medium">{entity.source || 'N/A'}</span></div>
            <div><span className="text-slate-500">Referral:</span> <span className="font-medium">{entity.referral_source || 'N/A'}</span></div>
            <div><span className="text-slate-500">Sales Rep:</span> <span className="font-medium">{entity.sales_rep || 'N/A'}</span></div>
            <div><span className="text-slate-500">Lead Score:</span> <span className="font-medium">{entity.lead_score || 'N/A'}</span></div>
            <div><span className="text-slate-500">Expected Close:</span> <span className="font-medium">{formatDate(entity.expected_close_date)}</span></div>
            <div><span className="text-slate-500">Contract Value:</span> <span className="font-medium">{entity.contract_value || 'N/A'}</span></div>
            <div><span className="text-slate-500">Follow-up Date:</span> <span className="font-medium">{formatDate(entity.follow_up_date)}</span></div>
          </div>
        </div>
      )}

      {/* OTHER INFO SECTION */}
      {showOtherInfo && (
        <div className={`border-t ${cc.border} p-4 ${cc.bg}`}>
          <h4 className="font-semibold mb-3 flex items-center"><FileText size={16} className="mr-2" />Other Information</h4>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Tags:</span> <span className="font-medium">{entity.tags || 'N/A'}</span></div>
            <div><span className="text-slate-500">Description:</span> <span className="font-medium">{entity.description || 'N/A'}</span></div>
            {(type === 'prospect' || type === 'customer') && entity.assistant_first_name && (
              <div><span className="text-slate-500">Assistant:</span> <span className="font-medium">{entity.assistant_first_name} {entity.assistant_last_name} - {entity.assistant_phone} - {entity.assistant_email}</span></div>
            )}
          </div>
        </div>
      )}

      {/* JOBS SECTION */}
      {showJobs && (
        <div className={`border-t ${cc.border}`}>
          <div className={`${cc.bg} p-4`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold flex items-center"><FolderOpen size={18} className="mr-2" />JOBS ({entity.jobs?.length || 0})</h4>
              <button onClick={() => setShowAddJob(!showAddJob)} className={`${cc.btn} text-white px-3 py-1.5 rounded text-sm flex items-center`}><Plus size={16} className="mr-1" />{showAddJob ? 'Cancel' : 'Add Job'}</button>
            </div>
            {showAddJob && (
              <div className="bg-white p-4 rounded-lg border mb-4">
                <div className="grid md:grid-cols-3 gap-3 mb-3">
                  <input type="text" value={jobForm.job_name} onChange={(e) => setJobForm({ ...jobForm, job_name: e.target.value })} placeholder="Job Name *" className="px-3 py-2 border rounded text-sm" />
                  <select value={jobForm.status} onChange={(e) => setJobForm({ ...jobForm, status: e.target.value })} className="px-3 py-2 border rounded text-sm">
                    <option value="pending">Pending</option><option value="active">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                  </select>
                  <input type="text" value={jobForm.contact_person} onChange={(e) => setJobForm({ ...jobForm, contact_person: e.target.value })} placeholder="Contact Person" className="px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid md:grid-cols-3 gap-3 mb-3">
                  <div><label className="text-xs text-slate-600">Start Date</label><input type="date" value={jobForm.start_date} onChange={(e) => setJobForm({ ...jobForm, start_date: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
                  <div><label className="text-xs text-slate-600">End Date</label><input type="date" value={jobForm.end_date} onChange={(e) => setJobForm({ ...jobForm, end_date: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
                  <input type="text" value={jobForm.timeline} onChange={(e) => setJobForm({ ...jobForm, timeline: e.target.value })} placeholder="Timeline" className="px-3 py-2 border rounded text-sm mt-auto" />
                </div>
                <textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} placeholder="Description" rows="2" className="w-full px-3 py-2 border rounded text-sm mb-3" />
                <button onClick={addJob} disabled={isSubmitting} className={`${cc.btn} text-white px-4 py-2 rounded text-sm`}>Save Job</button>
              </div>
            )}
            {entity.jobs?.length > 0 ? (
              <div className="space-y-2">
                {entity.jobs.map((job) => (
                  <div key={job.id} className={`bg-white border rounded p-3 ${selectedJob?.id === job.id ? 'border-green-500' : ''}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <button onClick={() => setSelectedJob(selectedJob?.id === job.id ? null : job)}>{selectedJob?.id === job.id ? <CheckSquare size={16} className="text-green-600" /> : <Square size={16} className="text-slate-400" />}</button>
                          <span className="font-semibold">{job.job_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${job.status === 'completed' ? 'bg-blue-100 text-blue-700' : job.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{job.status}</span>
                        </div>
                        <div className="text-xs text-slate-500 ml-6">{job.start_date && <span>Start: {job.start_date}</span>} {job.end_date && <span className="ml-2">End: {job.end_date}</span>}</div>
                      </div>
                      <button onClick={() => deleteJob(job.id)} className="text-red-500"><Trash2 size={16} /></button>
                    </div>
                    {selectedJob?.id === job.id && (
                      <div className="mt-3 pt-3 border-t ml-6 space-y-4">
                        {job.description && <p className="text-sm mb-2 text-slate-700">{job.description}</p>}
                        
                        {/* TIMELINE SECTION */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <h5 className="font-medium text-sm flex items-center"><Clock size={14} className="mr-1" /> Timeline Updates ({job.timeline_updates?.length || 0})</h5>
                            <button onClick={() => setShowTimelineForm(!showTimelineForm)} className="text-xs text-blue-600 hover:underline">{showTimelineForm ? 'Cancel' : '+ Add Update'}</button>
                          </div>
                          {showTimelineForm && (
                            <div className="mb-3 p-2 bg-white rounded border">
                              <div className="flex gap-2 mb-2">
                                <select value={timelineForm.update_type} onChange={(e) => setTimelineForm({...timelineForm, update_type: e.target.value})} className="px-2 py-1 border rounded text-xs">
                                  <option value="progress">Progress</option>
                                  <option value="milestone">Milestone</option>
                                  <option value="issue">Issue</option>
                                  <option value="note">Note</option>
                                </select>
                                <input type="text" value={timelineForm.description} onChange={(e) => setTimelineForm({...timelineForm, description: e.target.value})} placeholder="Update description..." className="flex-1 px-2 py-1 border rounded text-xs" />
                                <button onClick={() => addTimelineUpdate(job.id)} disabled={isSubmitting} className={`${cc.btn} text-white px-2 py-1 rounded text-xs`}>Add</button>
                              </div>
                            </div>
                          )}
                          {job.timeline_updates?.length > 0 ? (
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {job.timeline_updates.slice().reverse().map((u) => (
                                <div key={u.id} className="bg-white p-2 rounded text-xs flex justify-between items-start border">
                                  <div className="flex items-start gap-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${u.update_type === 'milestone' ? 'bg-green-100 text-green-700' : u.update_type === 'issue' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {u.update_type === 'milestone' ? <CheckCircle size={10} className="inline mr-0.5" /> : u.update_type === 'issue' ? <AlertCircle size={10} className="inline mr-0.5" /> : <Clock size={10} className="inline mr-0.5" />}
                                      {u.update_type}
                                    </span>
                                    <div>
                                      <span className="text-slate-400">{new Date(u.timestamp).toLocaleString()}</span>
                                      <p className="text-slate-700">{u.description}</p>
                                    </div>
                                  </div>
                                  <button onClick={() => deleteTimelineUpdate(job.id, u.id)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                                </div>
                              ))}
                            </div>
                          ) : <p className="text-slate-400 text-xs text-center py-2">No timeline updates yet.</p>}
                        </div>

                        {/* FILES/DOCUMENTS SECTION */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <h5 className="font-medium text-sm flex items-center mb-2"><FileText size={14} className="mr-1" /> Files & Documents ({job.files?.length || 0})</h5>
                          <div className="flex gap-2 mb-3 flex-wrap">
                            <select value={fileCategory} onChange={(e) => setFileCategory(e.target.value)} className="px-2 py-1 border rounded text-xs bg-white">
                              <option value="blueprint">Blueprint</option>
                              <option value="drawing">Drawing</option>
                              <option value="document">Document</option>
                              <option value="image">Image</option>
                              <option value="other">Other</option>
                            </select>
                            <input 
                              type="file" 
                              ref={fileInputRef}
                              onChange={(e) => uploadJobFile(job.id, e.target.files[0])}
                              className="text-xs"
                              disabled={uploadingFile}
                            />
                            {uploadingFile && <span className="text-xs text-blue-600 animate-pulse">Uploading...</span>}
                          </div>
                          {job.files?.length > 0 ? (
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {job.files.map((f) => (
                                <div key={f.id} className="bg-white p-2 rounded text-xs flex justify-between items-center border">
                                  <div className="flex items-center gap-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${f.file_type === 'blueprint' ? 'bg-blue-100 text-blue-700' : f.file_type === 'drawing' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}`}>{f.file_type}</span>
                                    <span className="text-slate-700">{f.original_name}</span>
                                    <span className="text-slate-400">({new Date(f.uploaded_at).toLocaleDateString()})</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <a href={`${BACKEND_URL}${f.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center"><Download size={12} className="mr-0.5" />View</a>
                                    <button onClick={() => deleteJobFile(job.id, f.id)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : <p className="text-slate-400 text-xs text-center py-2">No files uploaded yet.</p>}
                        </div>

                        {/* NOTES SECTION */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <h5 className="font-medium text-sm flex items-center mb-2"><StickyNote size={14} className="mr-1" /> Job Notes ({job.job_notes?.length || 0})</h5>
                          <div className="flex gap-2 mb-2">
                            <input type="text" value={jobNote} onChange={(e) => setJobNote(e.target.value)} placeholder="Add note..." className="flex-1 px-2 py-1 border rounded text-xs" />
                            <button onClick={() => addJobNote(job.id)} disabled={isSubmitting} className={`${cc.btn} text-white px-2 py-1 rounded text-xs`}>Add</button>
                          </div>
                          {job.job_notes?.length > 0 ? (
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {job.job_notes.slice().reverse().map((n) => (
                                <div key={n.timestamp} className="bg-white p-2 rounded text-xs border">
                                  <span className="text-slate-400">{new Date(n.timestamp).toLocaleString()}</span>: {n.note}
                                </div>
                              ))}
                            </div>
                          ) : <p className="text-slate-400 text-xs text-center py-2">No notes yet.</p>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-center py-4">No jobs yet.</p>}
          </div>
          <div className="p-4 border-t">
            <h4 className="font-semibold mb-2"><StickyNote size={16} className="inline mr-2" />GENERAL NOTES ({entity.notes_list?.length || 0})</h4>
            <div className="flex gap-2 mb-2">
              <input type="text" value={generalNote} onChange={(e) => setGeneralNote(e.target.value)} placeholder="Add note..." className="flex-1 px-3 py-2 border rounded text-sm" />
              <button onClick={addGeneralNote} className={`${cc.btn} text-white px-4 py-2 rounded text-sm`}>Add</button>
            </div>
            {entity.notes_list?.length > 0 && <div className="space-y-1 max-h-32 overflow-y-auto">{entity.notes_list.slice().reverse().map((note) => (<div key={note.id} className="bg-slate-50 p-2 rounded text-sm flex justify-between"><div><span className="text-slate-500 text-xs">{new Date(note.timestamp).toLocaleString()}</span><p>{note.text}</p></div><button onClick={() => deleteGeneralNote(note.id)} className="text-red-500"><Trash2 size={14} /></button></div>))}</div>}
          </div>
        </div>
      )}

      {/* CALLBACKS SECTION */}
      {showCallbacks && (
        <div className={`border-t ${cc.border}`}>
          <div className="p-4 bg-yellow-50">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold flex items-center"><PhoneCall size={18} className="mr-2" />CALLBACKS ({entity.callbacks?.length || 0})</h4>
              <button onClick={() => setShowAddCallback(!showAddCallback)} className={`${cc.btn} text-white px-3 py-1.5 rounded text-sm flex items-center`}><Plus size={16} className="mr-1" />{showAddCallback ? 'Cancel' : 'Add Callback'}</button>
            </div>
            {showAddCallback && (
              <div className="bg-white p-4 rounded-lg border mb-4">
                <div className="grid md:grid-cols-3 gap-3 mb-3">
                  <div><label className="text-xs text-slate-600">Date *</label><input type="date" value={callbackForm.callback_date} onChange={(e) => setCallbackForm({ ...callbackForm, callback_date: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
                  <div><label className="text-xs text-slate-600">Time</label><input type="time" value={callbackForm.callback_time} onChange={(e) => setCallbackForm({ ...callbackForm, callback_time: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
                  <select value={callbackForm.status} onChange={(e) => setCallbackForm({ ...callbackForm, status: e.target.value })} className="px-3 py-2 border rounded text-sm mt-auto">
                    <option value="scheduled">Scheduled</option><option value="completed">Completed</option><option value="missed">Missed</option><option value="rescheduled">Rescheduled</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-3 gap-3 mb-3">
                  <input type="text" value={callbackForm.contact_person} onChange={(e) => setCallbackForm({ ...callbackForm, contact_person: e.target.value })} placeholder="Contact Person" className="px-3 py-2 border rounded text-sm" />
                  <input type="tel" value={callbackForm.phone} onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })} placeholder="Phone" className="px-3 py-2 border rounded text-sm" />
                  <input type="email" value={callbackForm.email} onChange={(e) => setCallbackForm({ ...callbackForm, email: e.target.value })} placeholder="Email" className="px-3 py-2 border rounded text-sm" />
                </div>
                <textarea value={callbackForm.notes} onChange={(e) => setCallbackForm({ ...callbackForm, notes: e.target.value })} placeholder="Notes" rows="2" className="w-full px-3 py-2 border rounded text-sm mb-3" />
                <button onClick={addCallback} disabled={isSubmitting} className={`${cc.btn} text-white px-4 py-2 rounded text-sm`}>Save Callback</button>
              </div>
            )}
            {entity.callbacks?.length > 0 ? (
              <div className="space-y-2">
                {entity.callbacks.map((cb) => (
                  <div key={cb.id} className="bg-white border rounded p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center space-x-2">
                          <Calendar size={16} className="text-yellow-600" />
                          <span className="font-semibold">{cb.callback_date} {cb.callback_time && `at ${cb.callback_time}`}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${cb.status === 'completed' ? 'bg-green-100 text-green-700' : cb.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{cb.status}</span>
                        </div>
                        {cb.contact_person && <p className="text-sm text-slate-600 ml-6">{cb.contact_person} {cb.phone && `• ${cb.phone}`} {cb.email && `• ${cb.email}`}</p>}
                        {cb.notes && <p className="text-sm text-slate-500 ml-6 mt-1">{cb.notes}</p>}
                      </div>
                      <button onClick={() => deleteCallback(cb.id)} className="text-red-500"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-center py-4">No callbacks yet.</p>}
          </div>
        </div>
      )}

      {/* TEMPLATES SECTION */}
      {showTemplates && (
        <div className={`border-t ${cc.border}`}>
          <div className="p-4 bg-slate-50">
            <h4 className="font-semibold flex items-center mb-4"><Mail size={18} className="mr-2" />EMAIL TEMPLATES ({templates?.length || 0})</h4>
            {templates?.length > 0 ? (
              <div className="space-y-2">
                {templates.map((template) => (
                  <div key={template.id} className="bg-white border rounded p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div><h5 className="font-semibold">{template.name}</h5><p className="text-sm text-slate-600">Subject: {template.subject}</p></div>
                      <button className={`${cc.btn} text-white px-3 py-1 rounded text-sm flex items-center`}><Send size={14} className="mr-1" />Use</button>
                    </div>
                    <div className="bg-slate-50 p-2 rounded text-sm max-h-24 overflow-y-auto">{template.body}</div>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-center py-4">No templates available. Create templates in the Templates tab.</p>}
          </div>
        </div>
      )}

      {/* CONTACTS SECTION */}
      {showContacts && (
        <div className={`border-t ${cc.border}`}>
          <div className="p-4 bg-purple-50">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold flex items-center"><UserPlus size={18} className="mr-2" />CONTACTS ({entity.contacts?.length || 0})</h4>
              <button onClick={() => setShowAddContact(!showAddContact)} className={`${cc.btn} text-white px-3 py-1.5 rounded text-sm flex items-center`}><Plus size={16} className="mr-1" />{showAddContact ? 'Cancel' : 'Add Contact'}</button>
            </div>
            {showAddContact && (
              <div className="bg-white p-4 rounded-lg border mb-4">
                <div className="grid md:grid-cols-3 gap-3 mb-3">
                  <input type="text" value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} placeholder="First Name *" className="px-3 py-2 border rounded text-sm" />
                  <input type="text" value={contactForm.last_name} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} placeholder="Last Name *" className="px-3 py-2 border rounded text-sm" />
                  <input type="text" value={contactForm.job_title} onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })} placeholder="Job Title" className="px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Email" className="px-3 py-2 border rounded text-sm" />
                  <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="Phone" className="px-3 py-2 border rounded text-sm" />
                </div>
                <textarea value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} placeholder="Notes" rows="2" className="w-full px-3 py-2 border rounded text-sm mb-3" />
                <div className="flex items-center mb-3">
                  <input type="checkbox" checked={contactForm.is_primary} onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })} className="mr-2" />
                  <label className="text-sm">Primary Contact</label>
                </div>
                <button onClick={addContact} disabled={isSubmitting} className={`${cc.btn} text-white px-4 py-2 rounded text-sm`}>Save Contact</button>
              </div>
            )}
            {entity.contacts?.length > 0 ? (
              <div className="space-y-2">
                {entity.contacts.map((contact) => (
                  <div key={contact.id} className="bg-white border rounded p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold">{contact.first_name} {contact.last_name}</span>
                          {contact.is_primary && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Primary</span>}
                          {contact.job_title && <span className="text-xs text-slate-500">({contact.job_title})</span>}
                        </div>
                        <p className="text-sm text-slate-600">{contact.email && contact.email} {contact.phone && `• ${contact.phone}`}</p>
                        {contact.notes && <p className="text-sm text-slate-500 mt-1">{contact.notes}</p>}
                      </div>
                      <button onClick={() => deleteContact(contact.id)} className="text-red-500"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-center py-4">No contacts yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

// Callback Response Form

export default ExpandableEntityCard;
